/**
 * /checkin/admin/operations — Check In Operations workspace (Phase 1.5 patch).
 *
 * Three tabs:
 *   · Settings         — all 17 Phase 1.5 settings + restore-defaults + Twilio health
 *   · Merge Reviews    — duplicate-phone queue with full account snapshots + actions
 *   · Analytics        — trust & conversion metrics with date filters
 *
 * Uses the existing Check-In admin JWT (role='checkin_admin'). No new
 * auth model. Redirects to /checkin/admin/signin when unauthenticated.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CHECKIN_PALETTE as P } from "../_shell";
import { checkinAdminOps } from "@/lib/checkinAdminOps";
import { getToken, clearAuth } from "@/lib/checkinApi";

const TABS = [
  { id: "settings", label: "Phase 1.5 Settings" },
  { id: "merge", label: "Phone Merge Reviews" },
  { id: "analytics", label: "Trust & Conversion Analytics" },
];

const SETTING_GROUPS = [
  {
    title: "Identity & Verification",
    fields: [
      { key: "require_phone_verification", label: "Require phone verification", type: "bool" },
      { key: "sms_provider", label: "SMS provider mode", type: "select",
        options: [["dev", "Development (log-only)"], ["twilio", "Twilio (production)"]] },
      { key: "verification_code_expiration_seconds", label: "Verification code expiration (seconds)",
        type: "number", min: 60, max: 3600 },
      { key: "max_verification_attempts", label: "Maximum verification attempts per code",
        type: "number", min: 1, max: 20 },
      { key: "grace_period_enabled", label: "Existing-user grace period enabled", type: "bool" },
      { key: "grace_login_count", label: "Grace login count", type: "number", min: 0, max: 20 },
    ],
  },
  {
    title: "Complimentary Session",
    fields: [
      { key: "complimentary_user_messages", label: "Complimentary user-message limit",
        type: "number", min: 1, max: 20 },
      { key: "complimentary_dr_c_responses", label: "Complimentary Dr. C response limit",
        type: "number", min: 1, max: 20 },
      { key: "soft_reminder_threshold", label: "Soft-reminder threshold (Dr. C response #)",
        type: "number", min: 0, max: 20 },
    ],
  },
  {
    title: "Relationship Features",
    fields: [
      { key: "conversation_summary_enabled", label: "Conversation summary enabled", type: "bool" },
      { key: "suggested_next_checkin_enabled", label: "Suggested Next Check-In enabled", type: "bool" },
      { key: "journey_timeline_enabled", label: "Journey Timeline enabled", type: "bool" },
      { key: "relationship_memory_enabled", label: "Relationship Memory enabled", type: "bool" },
      { key: "intelligent_returning_greeting_enabled", label: "Intelligent returning greeting enabled",
        type: "bool" },
    ],
  },
  {
    title: "Experiments & Analytics",
    fields: [
      { key: "experiment_enabled", label: "Experiment framework enabled", type: "bool" },
      { key: "trust_score_enabled", label: "Trust score recomputation enabled", type: "bool" },
    ],
  },
];

export default function CheckInOperations() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    if (!getToken()) { setAuthed("redirect"); return; }
    (async () => {
      try {
        await checkinAdminOps.getSettings();
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  if (authed === null) {
    return <FullScreen>Checking access…</FullScreen>;
  }
  if (authed === "redirect" || authed === false) {
    return <FullScreen>
      <div className="text-center">
        <div className="text-sm mb-3">Please sign in as the Check In admin.</div>
        <Link to="/checkin/admin/signin?next=/checkin/admin/operations"
              className="px-3 py-2 rounded-lg text-white text-sm"
              style={{ background: P.purple }}
              data-testid="ops-signin-link">Sign in</Link>
      </div>
    </FullScreen>;
  }

  return (
    <div style={{ background: P.warmBg, minHeight: "100vh", color: P.ink }}
         data-testid="ops-workspace">
      <Header />
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="mt-4 flex gap-2 border-b" style={{ borderColor: P.silverSoft }}
             data-testid="ops-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`ops-tab-${t.id}`}
                    className="px-4 py-2 text-sm"
                    style={{
                      borderBottom: tab === t.id ? `2px solid ${P.purple}` : "2px solid transparent",
                      color: tab === t.id ? P.ink : "#6B6373",
                      fontWeight: tab === t.id ? 600 : 400,
                    }}>{t.label}</button>
          ))}
        </div>

        {tab === "settings" && <SettingsTab />}
        {tab === "merge" && <MergeTab />}
        {tab === "analytics" && <AnalyticsTab />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="border-b" style={{ background: "#FFFFFF", borderColor: P.silverSoft }}>
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: P.purple }}>
            Check In Admin
          </div>
          <div className="text-xl font-semibold mt-1">Check In Operations</div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/checkin/admin" className="text-xs underline" style={{ color: P.purple }}
                data-testid="ops-back-admin">← Admin console</Link>
        </div>
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div style={{ background: P.warmBg, color: P.ink, minHeight: "100vh" }}
         className="flex items-center justify-center px-6">
      {children}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Settings tab
// ═══════════════════════════════════════════════════════════

function SettingsTab() {
  const [state, setState] = useState(null);
  const [twilio, setTwilio] = useState(null);
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [error, setError] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setError(""); setFlash("");
    try {
      const [s, t] = await Promise.all([
        checkinAdminOps.getSettings(), checkinAdminOps.twilioHealth(),
      ]);
      setState(s); setTwilio(t); setPending({});
    } catch (e) { setError(e.message || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const change = (key, val) => setPending((p) => ({ ...p, [key]: val }));

  const save = async () => {
    if (Object.keys(pending).length === 0) { setFlash("No changes to save."); return; }
    setBusy(true); setError(""); setFlash("");
    try {
      const r = await checkinAdminOps.patchSettings(pending);
      setState({ ...state, settings: r.settings });
      setPending({});
      setFlash(`Saved ${r.changes.length} setting${r.changes.length === 1 ? "" : "s"}.`);
    } catch (e) {
      const d = e.detail || {};
      setError(d.code ? `Validation error on ${d.key || "setting"}: ${d.code}` : (e.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!window.confirm("Restore all Phase 1.5 settings to their recommended defaults?")) return;
    setBusy(true); setError(""); setFlash("");
    try {
      const r = await checkinAdminOps.restoreDefaults();
      setState({ ...state, settings: r.settings });
      setPending({});
      setFlash(`Restored defaults on ${r.restored} setting${r.restored === 1 ? "" : "s"}.`);
    } catch (e) { setError(e.message || "Restore failed"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTestResult(null); setError(""); setTestBusy(true);
    try {
      const r = await checkinAdminOps.twilioTestSend(testPhone,
        (twilio?.sms_mode === "dev"));
      setTestResult(r);
    } catch (e) {
      setError(e?.detail?.message || e?.detail?.code || e.message || "Test send failed");
    } finally { setTestBusy(false); }
  };

  if (!state) return <div className="mt-6 text-sm" style={{ color: "#5F5766" }}>Loading…</div>;

  const settings = { ...state.settings, ...pending };
  const dirty = Object.keys(pending).length > 0;

  return (
    <div className="mt-6 space-y-6">
      {/* Twilio health */}
      <div className="rounded-2xl p-4"
           style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
           data-testid="ops-twilio-health">
        <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
          SMS Provider Health
        </div>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-2xl font-semibold"
                data-testid="ops-twilio-status">
            {twilio?.status === "configured" ? "Twilio Production" :
              twilio?.status === "dev_mode" ? "Development Mode" :
              "Misconfigured"}
          </span>
          <span className="text-[11px] px-2 py-0.5 rounded-full"
                style={{
                  background: twilio?.status === "configured" ? "#E1F6E7" :
                              twilio?.status === "dev_mode" ? "#FDF6E4" : "#FEEEF1",
                  color: twilio?.status === "configured" ? "#146A2A" :
                          twilio?.status === "dev_mode" ? "#8B6B14" : P.danger,
                }}>
            {twilio?.sms_mode === "twilio" ? "TWILIO" : "DEV"}
          </span>
        </div>
        {twilio?.warning && (
          <div className="mt-2 text-sm" style={{ color: "#8B6B14" }}
               data-testid="ops-twilio-warning">{twilio.warning}</div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          {Object.entries(twilio?.env || {}).map(([k, v]) => (
            <div key={k}
                 className="rounded-lg px-2 py-1"
                 data-testid={`ops-env-${k}`}
                 style={{
                   background: v ? "#E1F6E7" : "#FEEEF1",
                   color: v ? "#146A2A" : P.danger,
                 }}>
              {v ? "✓ present" : "✗ missing"} · {k}
            </div>
          ))}
        </div>
        {/* Test send */}
        <div className="mt-4 flex gap-2 items-center">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
                 placeholder="+1 415 555 2671"
                 data-testid="ops-twilio-test-phone"
                 className="px-3 py-2 rounded-lg text-sm flex-1"
                 style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
          <button onClick={sendTest} disabled={testBusy || !testPhone}
                  data-testid="ops-twilio-test-btn"
                  className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                  style={{ background: P.purple }}>
            {testBusy ? "Sending…" : "Send verification test"}
          </button>
        </div>
        {testResult && (
          <div className="mt-2 text-xs" style={{ color: "#5F5766" }}
               data-testid="ops-twilio-test-result">
            Sent · mode={testResult.mode} · to {testResult.phone_masked}
          </div>
        )}
      </div>

      {/* Grouped settings */}
      {SETTING_GROUPS.map((g) => (
        <div key={g.title}
             className="rounded-2xl p-4"
             style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
             data-testid={`ops-settings-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
            {g.title}
          </div>
          <div className="mt-3 space-y-3">
            {g.fields.map((f) => (
              <SettingRow key={f.key} field={f} value={settings[f.key]}
                           pending={f.key in pending}
                           onChange={(v) => change(f.key, v)} />
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3 sticky bottom-0 py-3"
           style={{ background: P.warmBg }}>
        <button onClick={save} disabled={!dirty || busy}
                data-testid="ops-settings-save-btn"
                className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ background: P.purple }}>
          {busy ? "Saving…" : `Save ${dirty ? `(${Object.keys(pending).length})` : "changes"}`}
        </button>
        <button onClick={restore} disabled={busy}
                data-testid="ops-settings-restore-btn"
                className="px-3 py-2 rounded-lg text-sm disabled:opacity-50"
                style={{ background: P.silverSoft, color: P.ink }}>
          Restore recommended defaults
        </button>
        {flash && <span className="text-xs" style={{ color: "#146A2A" }}
                         data-testid="ops-settings-flash">{flash}</span>}
        {error && <span className="text-xs" style={{ color: P.danger }}
                         data-testid="ops-settings-error">{error}</span>}
      </div>
    </div>
  );
}

function SettingRow({ field, value, pending, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3"
         data-testid={`ops-setting-${field.key}`}>
      <div className="text-sm">
        {field.label}
        {pending && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full"
                          style={{ background: "#FDF6E4", color: "#8B6B14" }}>unsaved</span>}
      </div>
      <div>
        {field.type === "bool" && (
          <label className="inline-flex items-center gap-2 text-xs" style={{ color: "#5F5766" }}>
            <input type="checkbox"
                   checked={!!value}
                   onChange={(e) => onChange(e.target.checked)}
                   data-testid={`ops-setting-input-${field.key}`} />
            {value ? "Enabled" : "Disabled"}
          </label>
        )}
        {field.type === "number" && (
          <input type="number" value={value ?? ""}
                 min={field.min} max={field.max}
                 onChange={(e) => onChange(Number(e.target.value))}
                 data-testid={`ops-setting-input-${field.key}`}
                 className="w-28 px-2 py-1 rounded-md text-sm"
                 style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
        )}
        {field.type === "select" && (
          <select value={value ?? ""}
                  onChange={(e) => onChange(e.target.value)}
                  data-testid={`ops-setting-input-${field.key}`}
                  className="px-2 py-1 rounded-md text-sm"
                  style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }}>
            {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Merge reviews tab
// ═══════════════════════════════════════════════════════════

function MergeTab() {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await checkinAdminOps.listMergeReviews(status);
      setData(r);
    } catch (e) { setError(e.message || "Failed to load"); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-6 space-y-4">
      <div className="flex gap-2 items-center text-xs" data-testid="ops-merge-filters">
        {["pending", "resolved", "rejected", "requires_verification"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  data-testid={`ops-merge-filter-${s}`}
                  className="px-3 py-1.5 rounded-full"
                  style={{
                    background: status === s ? P.purple : "#FFFFFF",
                    color: status === s ? "#FFFFFF" : P.ink,
                    border: `1px solid ${P.silverSoft}`,
                  }}>
            {s.replace(/_/g, " ")}
            {data?.counts?.[s] != null && <span className="ml-1 opacity-70">({data.counts[s]})</span>}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm p-3 rounded-lg"
             style={{ background: "#FEEEF1", color: P.danger }}>{error}</div>
      )}

      <div className="rounded-2xl"
           style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}>
        {(data?.reviews || []).length === 0 && (
          <div className="p-6 text-center text-sm" style={{ color: "#5F5766" }}
               data-testid="ops-merge-empty">
            No {status.replace(/_/g, " ")} reviews.
          </div>
        )}
        {(data?.reviews || []).map((r) => (
          <button key={r.id} onClick={() => setSelected(r)}
                  className="w-full text-left px-4 py-3 border-b block"
                  style={{ borderColor: P.silverSoft }}
                  data-testid={`ops-merge-row-${r.id}`}>
            <div className="flex justify-between text-sm">
              <div>
                <div className="font-medium">{r.phone_masked}</div>
                <div className="text-xs" style={{ color: "#5F5766" }}>
                  {r.requester?.email} → {r.owner?.email}
                </div>
              </div>
              <div className="text-[11px]" style={{ color: "#9E97AB" }}>
                {r.status}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <MergeReviewSheet review={selected} onClose={() => setSelected(null)}
                             onResolved={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function MergeReviewSheet({ review, onClose, onResolved }) {
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const runAction = async (action) => {
    setBusy(true); setError("");
    try {
      await checkinAdminOps.mergeAction(review.id, { action, notes });
      onResolved();
    } catch (e) {
      setError(e?.detail?.code || e.message || "Failed");
    } finally { setBusy(false); }
  };

  const actions = [
    ["confirm_merge_keep_existing", "Confirm same person & merge (keep existing)"],
    ["keep_separate", "Keep accounts separate"],
    ["transfer_to_existing", "Transfer verified phone to existing account"],
    ["transfer_to_attempting", "Transfer verified phone to attempting account"],
    ["require_additional_verification", "Require additional verification"],
    ["reject_request", "Reject request"],
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center"
         style={{ background: "rgba(11,7,21,0.55)" }}
         onClick={onClose}
         data-testid="ops-merge-sheet">
      <div className="w-full max-w-2xl rounded-t-3xl p-6"
           style={{ background: "#FFFFFF", maxHeight: "88vh", overflowY: "auto" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
              Merge review · {review.phone_masked}
            </div>
            <div className="text-lg font-semibold mt-1" data-testid="ops-merge-sheet-title">
              {review.conflict_reason}
            </div>
          </div>
          <button onClick={onClose}
                  data-testid="ops-merge-sheet-close"
                  className="text-2xl leading-none" style={{ color: "#9E97AB" }}>×</button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <AccountCard label="Existing account (owner)" acct={review.owner} />
          <AccountCard label="Attempting account (requester)" acct={review.requester} />
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
            Admin notes
          </div>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                     placeholder="What did you decide and why?"
                     data-testid="ops-merge-notes"
                     className="w-full mt-1 px-2 py-1 rounded-md text-sm"
                     style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
        </div>

        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: P.purple }}>
            Actions
          </div>
          <div className="space-y-2">
            {actions.map(([a, label]) => (
              <button key={a} onClick={() => setConfirming({ action: a, label })}
                      disabled={busy}
                      data-testid={`ops-merge-action-${a}`}
                      className="w-full py-2 px-3 rounded-lg text-sm text-left disabled:opacity-50"
                      style={{
                        background: a === "reject_request" ? "#FEEEF1" :
                                    a === "confirm_merge_keep_existing" ? "#E1F6E7" : "#F5F1FB",
                        color: a === "reject_request" ? P.danger : P.ink,
                        border: `1px solid ${P.silverSoft}`,
                      }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-3 text-sm p-3 rounded-lg"
               style={{ background: "#FEEEF1", color: P.danger }}>{error}</div>
        )}

        {confirming && (
          <div className="mt-4 rounded-xl p-4"
               style={{ background: "#FDF6E4", border: "1px solid #F1E3B0" }}
               data-testid="ops-merge-confirm">
            <div className="text-sm font-medium" style={{ color: "#8B6B14" }}>
              Confirm: {confirming.label}
            </div>
            <div className="text-xs mt-2" style={{ color: "#8B6B14" }}>
              {consequenceText(confirming.action)}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => runAction(confirming.action)} disabled={busy}
                      data-testid="ops-merge-confirm-yes"
                      className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                      style={{ background: P.purple }}>
                {busy ? "Working…" : "Confirm"}
              </button>
              <button onClick={() => setConfirming(null)} disabled={busy}
                      className="px-3 py-2 rounded-lg text-sm"
                      style={{ background: P.silverSoft, color: P.ink }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccountCard({ label, acct }) {
  return (
    <div className="rounded-xl p-3 text-xs"
         style={{ background: "#FBF9FE", border: `1px solid ${P.silverSoft}`, color: P.ink }}>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "#9E97AB" }}>{label}</div>
      <div className="mt-1 font-medium">{acct?.email || "—"}</div>
      <div className="text-[11px] mt-1" style={{ color: "#5F5766" }}>
        {acct?.phone_masked && <>Phone {acct.phone_masked} · </>}
        Created {acct?.created_at?.slice(0, 10) || "—"}
      </div>
      <div className="mt-2 text-[11px]">
        <div>Conversations: <b>{acct?.conversations_total ?? 0}</b></div>
        <div>Talk credits: <b>{acct?.talk_credit_balance ?? 0}</b></div>
        <div>Membership: <b>{acct?.membership_active ? "active" : "none"}</b></div>
        <div>Complimentary: <b>{acct?.complimentary_state || "available"}</b></div>
        {acct?.verified_at && <div>Verified {acct.verified_at.slice(0, 10)} ({acct.verification_method})</div>}
      </div>
    </div>
  );
}

function consequenceText(action) {
  switch (action) {
    case "confirm_merge_keep_existing":
      return "The existing (older) account remains primary. The attempting account is aliased so its verified phone is cleared and it cannot obtain a new complimentary session. Both accounts' conversations, credits, memberships, summaries, and timeline entries remain intact.";
    case "keep_separate":
      return "No changes are made. The review is marked resolved (kept separate).";
    case "transfer_to_existing":
      return "The phone stays with the existing account. The attempting account keeps its history but does not gain the phone.";
    case "transfer_to_attempting":
      return "The phone is cleared from the existing account (which loses its verified status and must re-verify). The attempting account must re-run /verify to bind the phone. Nothing else is changed.";
    case "require_additional_verification":
      return "The review is placed in a requires-verification queue. The attempting user is asked to prove identity via a follow-up channel.";
    case "reject_request":
      return "The request is rejected. Neither account is modified.";
    default: return "";
  }
}


// ═══════════════════════════════════════════════════════════
// Analytics tab
// ═══════════════════════════════════════════════════════════

function AnalyticsTab() {
  const [preset, setPreset] = useState("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cohort, setCohort] = useState("all");
  const [migration, setMigration] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const params = { cohort };
      if (from && to) { params.from_date = from; params.to_date = to; params.preset = ""; }
      else params.preset = preset;
      const [a, m] = await Promise.all([
        checkinAdminOps.analytics(params),
        checkinAdminOps.migrationSummary(),
      ]);
      setData(a); setMigration(m);
    } catch (e) { setError(e.message || "Failed to load"); }
  }, [preset, from, to, cohort]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const m = migration?.totals || {};

  return (
    <div className="mt-6 space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="ops-analytics-filters">
        {[["today", "Today"], ["7d", "7 days"], ["30d", "30 days"]].map(([p, l]) => (
          <button key={p} onClick={() => { setPreset(p); setFrom(""); setTo(""); }}
                  data-testid={`ops-preset-${p}`}
                  className="px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: preset === p && !from ? P.purple : "#FFFFFF",
                    color: preset === p && !from ? "#FFFFFF" : P.ink,
                    border: `1px solid ${P.silverSoft}`,
                  }}>{l}</button>
        ))}
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
               data-testid="ops-analytics-from"
               className="px-2 py-1 rounded-md text-xs"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
        <span className="text-xs">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
               data-testid="ops-analytics-to"
               className="px-2 py-1 rounded-md text-xs"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
        <select value={cohort} onChange={(e) => setCohort(e.target.value)}
                data-testid="ops-analytics-cohort"
                className="px-2 py-1 rounded-md text-xs"
                style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }}>
          <option value="all">All users</option>
          <option value="new_users">New users</option>
          <option value="existing_migrated">Existing migrated</option>
          <option value="experiment">Experiment cohort</option>
        </select>
      </div>

      {error && (
        <div className="text-sm p-3 rounded-lg"
             style={{ background: "#FEEEF1", color: P.danger }}>{error}</div>
      )}

      {/* Trust & Conversion tiles */}
      {data && (
        <div>
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: P.purple }}>
            Trust & Conversion · {data.range?.preset}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="New registrations" value={t.new_registrations} testid="tile-new-reg" />
            <Tile label="Verification sends" value={t.verification_attempts_sent} testid="tile-verify-sent" />
            <Tile label="Verifications completed" value={t.verifications_completed} testid="tile-verify-done" />
            <Tile label="Completion rate" value={`${t.verification_completion_rate_pct || 0}%`}
                   tone={P.purple} testid="tile-verify-rate" />
            <Tile label="Failure rate" value={`${t.verification_failure_rate_pct || 0}%`}
                   testid="tile-verify-fail" />
            <Tile label="Awaiting verification" value={t.existing_users_awaiting_verification}
                   testid="tile-awaiting" />
            <Tile label="Blocked after grace" value={t.existing_users_blocked_after_grace}
                   testid="tile-blocked" />
            <Tile label="Comp. sessions started" value={t.complimentary_sessions_started}
                   testid="tile-comp-started" />
            <Tile label="Comp. sessions completed" value={t.complimentary_sessions_completed}
                   testid="tile-comp-completed" />
            <Tile label="Soft reminders shown" value={t.soft_reminders_shown}
                   testid="tile-soft-reminders" />
            <Tile label="Summaries generated" value={t.conversation_summaries_generated}
                   testid="tile-summaries" />
            <Tile label="Journey entries" value={t.journey_entries} testid="tile-journey" />
            <Tile label="Talk-credit conversions" value={t.talk_credit_conversions}
                   testid="tile-tc-conv" />
            <Tile label="Membership conversions" value={t.membership_conversions}
                   testid="tile-mem-conv" />
            <Tile label="Est. AI cost" value={`$${(t.ai_cost_estimate_usd || 0).toFixed(2)}`}
                   testid="tile-ai-cost" />
            <Tile label="Est. voice cost" value={`$${(t.voice_cost_estimate_usd || 0).toFixed(2)}`}
                   testid="tile-voice-cost" />
          </div>
        </div>
      )}

      {/* Migration summary */}
      {migration && (
        <div>
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: P.purple }}>
            Existing-User Migration
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Total users" value={m.total_users} testid="tile-total-users" />
            <Tile label="Verified users" value={m.verified_users} testid="tile-verified-users" />
            <Tile label="In grace period" value={m.in_grace_period} testid="tile-in-grace" />
            <Tile label="1 login remaining" value={m.one_login_remaining} testid="tile-one-remaining" />
            <Tile label="Blocked (grace exhausted)" value={m.grace_exhausted} testid="tile-grace-exhausted" />
            <Tile label="Comp. unused" value={m.complimentary_unused} testid="tile-comp-unused" />
            <Tile label="Comp. partial" value={m.complimentary_partial} testid="tile-comp-partial" />
            <Tile label="Comp. consumed" value={m.complimentary_consumed} testid="tile-comp-consumed" />
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, testid, tone }) {
  return (
    <div className="rounded-2xl p-3"
         style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
         data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest" style={{ color: "#9E97AB" }}>{label}</div>
      <div className="text-2xl font-semibold mt-1"
           style={{ color: tone || P.ink }}>{value ?? 0}</div>
    </div>
  );
}
